import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import PropTypes from 'prop-types';

import { ndcDigits } from '../utils/bulkOverrideFilters';

/** Matches the drug name, or the NDC typed with or without its dashes. */
const filterChoices = createFilterOptions({
  stringify: (choice) => `${choice.drugName} ${choice.ndc} ${ndcDigits(choice.ndc)}`,
});

const describeRowCounts = ({ rowCount, needsAttentionCount }) =>
  [
    `${rowCount} row${rowCount === 1 ? '' : 's'}`,
    needsAttentionCount > 0
      ? `${needsAttentionCount} need${needsAttentionCount === 1 ? 's' : ''} attention`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

export const BULK_LOT_UPDATE_CHOICE_SHAPE = PropTypes.shape({
  key: PropTypes.string.isRequired,
  ndc: PropTypes.string.isRequired,
  drugName: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  needsAttentionCount: PropTypes.number.isRequired,
});

/**
 * POM-6537 — Bulk Lot Update's NDC: typed and picked, by drug name or NDC, so a batch of many NDCs
 * costs no more room than one of two. Each option says how many rows a lot can be given to and how
 * many of them need attention; the picked one says so beside it.
 *
 * Options keep the choices' order, A–Z by drug and then NDC (buildBulkLotUpdateChoices).
 */
const BulkLotUpdateNdcPicker = ({ choices, selectedKey, onSelect }) => {
  const selected = choices.find((choice) => choice.key === selectedKey) ?? null;

  return (
    <Box>
      <Typography
        component='label'
        htmlFor='bulk-lot-update-ndc'
        variant='subtitle1'
        sx={{ display: 'block', fontWeight: 700, lineHeight: 1.3, mb: 1 }}
      >
        NDC
      </Typography>
      <Autocomplete
        id='bulk-lot-update-ndc'
        options={choices}
        value={selected}
        onChange={(_event, choice) => choice && onSelect(choice.key)}
        filterOptions={filterChoices}
        getOptionLabel={(choice) => `${choice.drugName} · ${choice.ndc}`}
        isOptionEqualToValue={(option, value) => option.key === value.key}
        // An NDC is always needed, so there is nothing to clear back to.
        disableClearable
        autoHighlight
        openOnFocus
        noOptionsText='No NDC in this batch matches'
        renderOption={(props, choice) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <Box component='li' {...props} key={choice.key} sx={{ gap: 1.5 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant='body2' sx={{ fontWeight: 700 }}>
                {choice.drugName}
              </Typography>
              <Typography variant='body2' sx={{ fontFamily: 'monospace', color: 'grey.800' }}>
                {choice.ndc}
              </Typography>
            </Box>
            <Typography variant='body2' sx={{ color: 'grey.800', whiteSpace: 'nowrap' }}>
              {describeRowCounts(choice)}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            placeholder='Type a drug name or NDC'
            // Opens on the list when nothing is picked yet: picking the NDC is the first step.
            autoFocus={!selected}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {selected && (
                    <Typography
                      variant='body2'
                      sx={{ color: 'grey.800', whiteSpace: 'nowrap', flexShrink: 0, pl: 1 }}
                    >
                      {describeRowCounts(selected)}
                    </Typography>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{ '& .MuiInputBase-input': { fontWeight: selected ? 500 : 400 } }}
          />
        )}
      />
    </Box>
  );
};

BulkLotUpdateNdcPicker.propTypes = {
  choices: PropTypes.arrayOf(BULK_LOT_UPDATE_CHOICE_SHAPE).isRequired,
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

BulkLotUpdateNdcPicker.defaultProps = { selectedKey: null };

export default BulkLotUpdateNdcPicker;
